// ============================================================================
// kereso.js — keresés az üzenetekben (2026-10-04, tagi kérésre).
//
// MIÉRT KLIENSOLDALI ÁTNÉZÉS, és nem keresőindex?
// A Firestore nem tud szövegben keresni. Három út van: (1) külső keresőszolgáltatás
// (új költség, új adatút, új adatvédelmi kérdés), (2) saját token-index minden
// üzeneten (az összes RÉGI üzenetet vissza kellene töltenie egy migrációval,
// és a magyar ragozás miatt a pontos szó-egyezés gyenge), (3) a szoba üzeneteinek
// lapozott átnézése a kliensben. Ezt választottuk: nincs új infrastruktúra, nincs
// migráció, a RÉGI üzenetekben is azonnal működik, és részleges szóra
// ("kutat" -> "kutatás", "kutatásban") is talál.
//
// KÖLTSÉG: a Firestore olvasásonként számláz. Ezer üzenet átnézése ezer olvasás
// (~0,0006 USD). A rendszer 200 fős, ezért ez elfogadható; a lapokat egy rövid
// ideig gyorsítótárazzuk, hogy a keresőszó módosítása ne olvasson újra mindent.
// Ha a használat megnő, a következő lépés a szerveroldali index (lásd
// VALTOZASOK.md).
//
// A Firestore szabály szerint egy lista-lekérdezés legfeljebb 50 elemű
// (firestore.rules, messages/list), ezért a lapméret 50.
// ============================================================================

import { markdownSima } from "./markdown.js";

export const MIN_KARAKTER = 2;
export const MAX_SZAVAK = 8;
export const LAP_MERET = 50;

/**
 * Kis-nagybetű és ÉKEZET nélküli alak, a hozzá tartozó index-térképpel
 * (norm[i] az eredeti szöveg map[i]. karakteréből jött) - a kiemeléshez kell,
 * mert az ékezet-eltávolítás megváltoztathatja a szöveg hosszát.
 * Csak az ALAP kombináló jeleket (U+0300-036F) vesszük le: a japán ゛ (U+3099)
 * megkülönböztet jelentést (か / が), azt nem szabad.
 */
export function normalizalTerkepezve(szoveg) {
  const s = String(szoveg || "");
  let norm = "";
  const map = [];
  let i = 0;
  for (const ch of s) {
    const n = ch.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    for (const nc of n) { norm += nc; map.push(i); }
    i += ch.length;
  }
  return { norm, map };
}

export function normalizal(szoveg) {
  return normalizalTerkepezve(szoveg).norm;
}

/** A keresőkifejezés szavai (normalizálva). Üres tömb, ha túl rövid. */
export function keresesiSzavak(kifejezes) {
  const n = normalizal(kifejezes).trim();
  if ([...n].length < MIN_KARAKTER) return [];
  return [...new Set(n.split(/\s+/).filter(Boolean))].slice(0, MAX_SZAVAK);
}

function idopontMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  const n = Number(ts);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Egyezik-e az üzenet? MINDEN keresett szónak szerepelnie kell UGYANABBAN a
 * szövegben (a megjelenített - tartalomnyelvű - fordításban VAGY az eredetiben;
 * a kettő nem keveredik). A szövegek formázás nélküliek (markdownSima), hogy a
 * "**kutatás**" is találjon "kutatás"-ra.
 *
 * @returns {{szoveg: string, fordites: boolean} | null}
 */
export function talalat(uzenet, szavak, tartalomNyelv) {
  if (!uzenet || uzenet.deleted === true || szavak.length === 0) return null;
  const jeloltek = [];
  const fordites = uzenet.translations && typeof uzenet.translations[tartalomNyelv] === "string"
    ? uzenet.translations[tartalomNyelv] : "";
  if (fordites.trim()) jeloltek.push({ szoveg: fordites, fordites: true });
  if (typeof uzenet.text === "string" && uzenet.text.trim()) jeloltek.push({ szoveg: uzenet.text, fordites: false });
  for (const j of jeloltek) {
    const sima = markdownSima(j.szoveg);
    const norm = normalizal(sima);
    if (szavak.every((w) => norm.includes(w))) return { szoveg: sima, fordites: j.fordites };
  }
  return null;
}

/**
 * Részlet a találat köré, a keresett szavak kiemelésével.
 * @returns {{reszek: Array<{szoveg: string, kiemelt: boolean}>, elol: boolean, vegen: boolean}}
 */
export function reszlet(szoveg, szavak, hossz = 140) {
  const { norm, map } = normalizalTerkepezve(szoveg);
  const tartomanyok = [];
  for (const w of szavak) {
    let from = 0;
    while (w && (from = norm.indexOf(w, from)) !== -1) {
      const kezd = map[from];
      const utolso = from + w.length - 1;
      const vege = (utolso + 1 < map.length ? map[utolso + 1] : szoveg.length);
      tartomanyok.push([kezd, vege]);
      from += w.length;
    }
  }
  tartomanyok.sort((a, b) => a[0] - b[0]);
  // átfedők összevonása
  const egyesitett = [];
  for (const t of tartomanyok) {
    const utolso = egyesitett[egyesitett.length - 1];
    if (utolso && t[0] <= utolso[1]) utolso[1] = Math.max(utolso[1], t[1]);
    else egyesitett.push([...t]);
  }
  const elso = egyesitett.length ? egyesitett[0][0] : 0;
  let ablakKezd = Math.max(0, elso - Math.floor(hossz / 3));
  // szóhatáron kezdjük, ha lehet
  if (ablakKezd > 0) {
    const sp = szoveg.indexOf(" ", ablakKezd);
    if (sp !== -1 && sp - ablakKezd < 12 && sp < elso) ablakKezd = sp + 1;
  }
  const ablakVeg = Math.min(szoveg.length, ablakKezd + hossz);
  const reszek = [];
  let poz = ablakKezd;
  for (const [a, b] of egyesitett) {
    if (b <= ablakKezd || a >= ablakVeg) continue;
    const ka = Math.max(a, ablakKezd), kb = Math.min(b, ablakVeg);
    if (ka > poz) reszek.push({ szoveg: szoveg.slice(poz, ka), kiemelt: false });
    reszek.push({ szoveg: szoveg.slice(ka, kb), kiemelt: true });
    poz = kb;
  }
  if (poz < ablakVeg) reszek.push({ szoveg: szoveg.slice(poz, ablakVeg), kiemelt: false });
  return { reszek, elol: ablakKezd > 0, vegen: ablakVeg < szoveg.length };
}

/**
 * Lap-gyorsítótár a keresés alá (2026-10-04). A keresőszó módosítása így
 * nem olvassa újra a már átnézett üzeneteket. Egy lap legfeljebb `eletMs`-ig
 * érvényes (közben szerkeszthették/törölhették az üzeneteket), és a teljes
 * gyorsítótár legfeljebb `maxUzenet` üzenetet tart (túlcsorduláskor kiürül).
 */
export function lapGyorsitotar(lapLekeres, { eletMs = 5 * 60 * 1000, maxUzenet = 20000, ora = () => Date.now() } = {}) {
  const tar = new Map();
  let osszes = 0;
  const fuggveny = async (roomId, kurzor) => {
    const kulcs = `${roomId}|${kurzor ? kurzor.id : ""}`;
    const talalt = tar.get(kulcs);
    if (talalt && ora() - talalt.mikor < eletMs) return talalt.lap;
    const lap = await lapLekeres(roomId, kurzor);
    if (osszes + lap.uzenetek.length > maxUzenet) { tar.clear(); osszes = 0; }
    tar.set(kulcs, { lap, mikor: ora() });
    osszes += lap.uzenetek.length;
    return lap;
  };
  fuggveny.urit = () => { tar.clear(); osszes = 0; };
  return fuggveny;
}

/**
 * Folytatható keresés egy vagy több szobában. A szobák lapjait KÖRBEN kérjük
 * (egy kör = minden még nem kimerült szobából egy lap), így a találatok
 * nagyjából időrendben érkeznek, nem az első szoba kimerítése után.
 *
 * @param {object} p
 * @param {Array<{roomId: string, nev?: string}>} p.szobak
 * @param {(roomId: string, kurzor: any) => Promise<{uzenetek: any[], kurzor: any, vanTobb: boolean}>} p.lapLekeres
 * @param {string[]} p.szavak
 * @param {string} p.tartalomNyelv
 */
export function ujKereses({ szobak, lapLekeres, szavak, tartalomNyelv }) {
  const allapot = szobak.map((szoba) => ({ szoba, kurzor: null, kesz: false }));
  let vizsgalt = 0;
  let futo = false;

  return {
    get vizsgalt() { return vizsgalt; },
    get kesz() { return allapot.every((a) => a.kesz); },

    /**
     * Addig keres, amíg `maxTalalat` új találat nincs, vagy `maxLap` lapot
     * nem olvastunk, vagy a szobák el nem fogytak, vagy `megszakitva()` igaz.
     * @returns {Promise<{talalat: number, kesz: boolean, megszakitva: boolean}>}
     */
    async folytat({ maxTalalat = 30, maxLap = 40, megszakitva = () => false, ontalalat = () => {}, onhalad = () => {} } = {}) {
      if (futo) throw new Error("a keresés már fut");
      futo = true;
      let talalatok = 0, lapok = 0, megszakadt = false;
      try {
        while (!allapot.every((a) => a.kesz) && talalatok < maxTalalat && lapok < maxLap) {
          for (const a of allapot) {
            if (a.kesz) continue;
            if (megszakitva()) { megszakadt = true; break; }
            const lap = await lapLekeres(a.szoba.roomId, a.kurzor);
            if (megszakitva()) { megszakadt = true; break; }
            lapok++;
            vizsgalt += lap.uzenetek.length;
            a.kurzor = lap.kurzor;
            if (!lap.vanTobb || lap.uzenetek.length === 0) a.kesz = true;
            for (const uzenet of lap.uzenetek) {
              const t = talalat(uzenet, szavak, tartalomNyelv);
              if (t) {
                talalatok++;
                ontalalat({ szoba: a.szoba, uzenet, szoveg: t.szoveg, fordites: t.fordites, ido: idopontMs(uzenet.createdAt) });
              }
            }
            onhalad({ vizsgalt, kesz: allapot.every((x) => x.kesz) });
            if (talalatok >= maxTalalat || lapok >= maxLap) break;
          }
          if (megszakadt) break;
        }
      } finally {
        futo = false;
      }
      return { talalat: talalatok, kesz: allapot.every((a) => a.kesz), megszakitva: megszakadt };
    },
  };
}
